import "../../lib/element-matches-polyfill"; // Polyfill element.matches(); (e.g. for IE11)
import "../../lib/object-assign-polyfill"; // Polyfill Object.assign(); (e.g. for IE11)
import AjaxPageLink from "./AjaxPageLink";
import {default as AjaxPageConfig, defaultAjaxPageConfig} from "./AjaxPageConfig";

/**
 * All links use the default link config (which can be overriden using `setDefaultLinkConfig()` or
 * you can specify specific config to use by `addConfig()`. That config will extend the default which extends the default that is built in.
 * Multiple specific configs can match and will be applied in the order added.
 */
export default class AjaxPageLinkManager {

	private static instances: WeakMap<Element, AjaxPageLinkManager>;
	
	private mutationObserver: MutationObserver;
	private rootElement: Element;

	private static staticDefaultLinkConfig: AjaxPageConfig = defaultAjaxPageConfig;
	private defaultLinkConfig: AjaxPageConfig;

	private matchesConfigs: Map<string, AjaxPageConfig>;
	private links: HTMLCollectionOf<Element>;

	private linkStyleClass = "ajaxPageLink";
	private linkSetupStyleClass = "ajaxPageLink--isSetup";

	public static getInstance(rootElement: Element = window.document.documentElement): AjaxPageLinkManager {
		if (AjaxPageLinkManager.instances === undefined) {
			AjaxPageLinkManager.instances = new WeakMap<Element, AjaxPageLinkManager>();
		}
		let linkManager = AjaxPageLinkManager.instances.get(rootElement);
		if (linkManager === undefined) {
			linkManager = new AjaxPageLinkManager(rootElement);
			AjaxPageLinkManager.instances.set(rootElement, linkManager);
		}
		return linkManager;
	}

	private constructor(rootElement: Element) {
		this.rootElement = rootElement;
		this.checkForNewLinks = this.checkForNewLinks.bind(this);

		this.mutationObserver = new MutationObserver(this.checkForNewLinks);
		this.mutationObserver.observe(this.rootElement, {
			childList: true,
			subtree: true,
			characterData: false,
			attributes: false
		});

		this.links = this.rootElement.getElementsByClassName(this.linkStyleClass);
		this.checkForNewLinks(); // Call once
	}

	public setDefaultConfig(config: AjaxPageConfig) {
		this.defaultLinkConfig = config;
	}

	/**
	 * Add a specific config to override default settings.
	 * The `matches` is a query selector which the element link must
	 * match to get the config applied to it.
	 */
	public addConfig(matches: string, config: AjaxPageConfig) {
		if (this.matchesConfigs === undefined) {
			this.matchesConfigs = new Map<string, AjaxPageConfig>();
		}
		this.matchesConfigs.set(matches, config);
	}
	public removeConfig(matches: string) {
		if (this.matchesConfigs === undefined) {
			return;
		}
		this.matchesConfigs.delete(matches);
	}

	private getConfig(element: Element): AjaxPageConfig {
		const configsToMerge = new Array<Object>();
		configsToMerge.push({}); // Needed as first argument to "Object.apply();"
		configsToMerge.push(AjaxPageLinkManager.staticDefaultLinkConfig);
		configsToMerge.push(this.defaultLinkConfig);

		if (this.matchesConfigs !== undefined) {
			this.matchesConfigs.forEach((config: AjaxPageConfig, matches: string) => {
				if (element.matches(matches)) {
					configsToMerge.push(config)
				}
			});
		}
		return Object.assign.apply(null, configsToMerge);
	}
	
	private checkForNewLinks() {
		for (let i = 0; i < this.links.length; i++) {
			const element = this.links[i];
			if (element.classList.contains(this.linkSetupStyleClass) === false) {
				if (element instanceof HTMLAnchorElement) {
					new AjaxPageLink(
						element as HTMLAnchorElement,
						() => {
							return this.getConfig(element);
						}
					);
					element.classList.add(this.linkSetupStyleClass)
				} else {
					console.warn(`Element with class "${this.linkStyleClass}" is not an HTMLAnchorElement.`);
				}
			}
		}
	}
}